// Estado + carregamento de dados do "Histórico de cliente".
// Extraído de HistoricoCliente.vue sem mudança de contrato: mesmos endpoints,
// mesmos payloads, mesmos estados de carga e as mesmas mensagens de alerta.
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LeadHistoryAPI, {
  LEAD_HISTORY_LOAD_STATES,
  classifyLeadHistoryError,
  getLeadHistoryPayloadState,
  normalizeLeadHistoryPayload,
} from 'dashboard/api/leadHistory';
import OnlineDutyAPI from 'dashboard/api/onlineDuty';
import {
  CONVERSATION_STATE_COPY,
  HISTORY_STATE_COPY,
  NOTES_STATE_COPY,
  PROFILE_STATE_COPY,
  VISITAS_STATE_COPY,
} from '../utils/leadHistoryConstants';
import {
  arrayPayloadValue,
  collectionFromResponse,
  isUnavailableLoadState,
  loadStateCopy,
  nextSupportStatusFor,
  pendingSupportPayloadFromResponse,
  routeString,
  shouldApplyHistoryValue,
} from '../utils/leadHistoryPresenter';

export function useLeadHistoryData({ onHistoryPayloadApplied } = {}) {
  const route = useRoute();

  const defaultLead = () => ({
    name: 'Cliente',
    email: null,
    phone_number: null,
    qualification_status: null,
    additional_attributes: {},
    custom_attributes: {},
    blocked: false,
  });
  const lead = ref(defaultLead());
  const conversations = ref([]);
  const visitas = ref([]);
  const crmSummary = ref({});
  const buyerProfile = ref(null);
  const offers = ref([]);
  const favorites = ref([]);
  const recommendations = ref([]);
  const timeline = ref([]);
  const latestPendingSupport = ref(null);
  const publicVisitRequest = ref(null);
  const historyOriginPayload = ref({});
  const crmOperations = ref({});
  const selectedConversationPayload = ref(null);
  const conversationFeedback = ref(null);
  const selectedConversationId = ref(null);
  const messages = ref([]);
  const contactNotes = ref([]);
  const isLoading = ref(true);
  const isLoadingMessages = ref(false);
  const isLoadingConversationHistory = ref(false);
  const isLoadingNotes = ref(false);
  const profileLoadError = ref(false);
  const conversationLoadError = ref(false);
  const visitasLoadError = ref(false);
  const historyLoadState = ref(LEAD_HISTORY_LOAD_STATES.LOADING);
  const profileLoadState = ref(LEAD_HISTORY_LOAD_STATES.LOADING);
  const conversationLoadState = ref(LEAD_HISTORY_LOAD_STATES.LOADING);
  const visitasLoadState = ref(LEAD_HISTORY_LOAD_STATES.LOADING);
  const notesLoadState = ref(LEAD_HISTORY_LOAD_STATES.LOADING);
  const pendingSupportLoadState = ref(LEAD_HISTORY_LOAD_STATES.LOADING);
  const pendingSupportNextStatus = ref('complete');
  const refreshingTab = ref(null);
  const requestVersions = {
    messages: 0,
    history: 0,
    support: 0,
    visits: 0,
    notes: 0,
    page: 0,
  };

  const beginRequest = key => {
    requestVersions[key] += 1;
    return requestVersions[key];
  };

  const isCurrentRequest = (key, version, contactId) =>
    requestVersions[key] === version &&
    String(route.params.id || '') === String(contactId || '');

  const notifyHistoryPayloadApplied = () => {
    if (typeof onHistoryPayloadApplied === 'function') {
      onHistoryPayloadApplied();
    }
  };

  const leadHistoryStateFromError = error =>
    error?.leadHistoryState || classifyLeadHistoryError(error);

  const setLoadErrorState = (stateRef, error) => {
    const loadState = leadHistoryStateFromError(error);
    stateRef.value = loadState.kind;
    return loadState;
  };

  const updateCollectionState = (stateRef, collection) => {
    stateRef.value = collection.length
      ? LEAD_HISTORY_LOAD_STATES.READY
      : LEAD_HISTORY_LOAD_STATES.EMPTY;
  };

  const setLatestPendingSupportPayload = payload => {
    latestPendingSupport.value = payload || null;
    pendingSupportNextStatus.value = nextSupportStatusFor(payload);
  };

  const resetLeadData = () => {
    lead.value = defaultLead();
    conversations.value = [];
    visitas.value = [];
    crmSummary.value = {};
    buyerProfile.value = null;
    offers.value = [];
    favorites.value = [];
    recommendations.value = [];
    timeline.value = [];
    setLatestPendingSupportPayload(null);
    publicVisitRequest.value = null;
    historyOriginPayload.value = {};
    crmOperations.value = {};
    selectedConversationPayload.value = null;
    conversationFeedback.value = null;
    messages.value = [];
    contactNotes.value = [];
    isLoadingMessages.value = false;
    isLoadingConversationHistory.value = false;
    isLoadingNotes.value = false;
    notesLoadState.value = LEAD_HISTORY_LOAD_STATES.LOADING;
  };

  const clearSelectedConversationContext = () => {
    selectedConversationPayload.value = null;
    conversationFeedback.value = null;
  };

  const prependNote = note => {
    if (!note || typeof note !== 'object') return;

    contactNotes.value = [note, ...contactNotes.value];
    notesLoadState.value = LEAD_HISTORY_LOAD_STATES.READY;
  };

  const profileErrorCopy = computed(() =>
    loadStateCopy(PROFILE_STATE_COPY, profileLoadState.value)
  );
  const historyErrorCopy = computed(() =>
    loadStateCopy(HISTORY_STATE_COPY, historyLoadState.value)
  );
  const conversationErrorCopy = computed(() =>
    loadStateCopy(CONVERSATION_STATE_COPY, conversationLoadState.value)
  );
  const visitasErrorCopy = computed(() =>
    loadStateCopy(VISITAS_STATE_COPY, visitasLoadState.value)
  );
  const notesErrorCopy = computed(() =>
    loadStateCopy(NOTES_STATE_COPY, notesLoadState.value)
  );
  const historyLoadError = computed(() =>
    isUnavailableLoadState(historyLoadState.value)
  );
  const notesLoadError = computed(() =>
    isUnavailableLoadState(notesLoadState.value)
  );
  const pendingSupportLoadError = computed(() =>
    isUnavailableLoadState(pendingSupportLoadState.value)
  );
  const isRefreshingTab = tab => refreshingTab.value === tab;

  const carregarMensagens = async conversationId => {
    const contactId = route.params.id;
    const requestVersion = beginRequest('messages');

    if (!conversationId) {
      messages.value = [];
      conversationLoadState.value = LEAD_HISTORY_LOAD_STATES.EMPTY;
      return true;
    }

    isLoadingMessages.value = true;
    try {
      const { data } = await LeadHistoryAPI.messages(conversationId, {
        filter_internal_messages: true,
      });
      if (!isCurrentRequest('messages', requestVersion, contactId)) {
        return false;
      }

      conversationLoadError.value = false;
      messages.value = collectionFromResponse(data);
      updateCollectionState(conversationLoadState, messages.value);
      return true;
    } catch (error) {
      if (!isCurrentRequest('messages', requestVersion, contactId)) {
        return false;
      }

      conversationLoadError.value = true;
      const loadState = setLoadErrorState(conversationLoadState, error);
      useAlert(
        loadState.message ||
          error.response?.data?.error ||
          conversationErrorCopy.value.description
      );
      messages.value = [];
      return false;
    } finally {
      if (isCurrentRequest('messages', requestVersion, contactId)) {
        isLoadingMessages.value = false;
      }
    }
  };

  const applyHistoryPayload = (payload, { preserveMissing = false } = {}) => {
    const normalizedPayload = normalizeLeadHistoryPayload(payload);

    if (
      shouldApplyHistoryValue(normalizedPayload, 'summary', preserveMissing)
    ) {
      crmSummary.value = normalizedPayload.summary || {};
    }
    if (
      shouldApplyHistoryValue(
        normalizedPayload,
        'qualification_questions',
        preserveMissing
      ) &&
      normalizedPayload.qualification_questions
    ) {
      lead.value = {
        ...lead.value,
        qualification_questions: normalizedPayload.qualification_questions,
      };
    }
    if (
      shouldApplyHistoryValue(
        normalizedPayload,
        'buyer_profile',
        preserveMissing
      ) ||
      shouldApplyHistoryValue(normalizedPayload, 'lead', preserveMissing)
    ) {
      buyerProfile.value =
        normalizedPayload.buyer_profile ||
        normalizedPayload.lead?.buyer_profile ||
        (preserveMissing ? buyerProfile.value : null);
    }
    if (shouldApplyHistoryValue(normalizedPayload, 'offers', preserveMissing)) {
      offers.value = arrayPayloadValue(normalizedPayload.offers);
    }
    if (
      shouldApplyHistoryValue(normalizedPayload, 'favorites', preserveMissing)
    ) {
      favorites.value = arrayPayloadValue(normalizedPayload.favorites);
    }
    if (
      shouldApplyHistoryValue(
        normalizedPayload,
        'recommendations',
        preserveMissing
      )
    ) {
      recommendations.value = arrayPayloadValue(
        normalizedPayload.recommendations
      );
    }
    if (
      shouldApplyHistoryValue(normalizedPayload, 'timeline', preserveMissing)
    ) {
      timeline.value = arrayPayloadValue(normalizedPayload.timeline);
    }
    if (
      shouldApplyHistoryValue(
        normalizedPayload,
        'crm_operations',
        preserveMissing
      )
    ) {
      crmOperations.value =
        normalizedPayload.crm_operations ||
        (preserveMissing ? crmOperations.value : {});
    }
    if (
      shouldApplyHistoryValue(
        normalizedPayload,
        'public_visit_request',
        preserveMissing
      )
    ) {
      publicVisitRequest.value =
        normalizedPayload.public_visit_request ||
        (preserveMissing ? publicVisitRequest.value : null);
    }
    if (shouldApplyHistoryValue(normalizedPayload, 'origem', preserveMissing)) {
      historyOriginPayload.value = normalizedPayload.origem || {};
    }
    if (
      shouldApplyHistoryValue(
        normalizedPayload,
        'selected_conversation',
        preserveMissing
      )
    ) {
      selectedConversationPayload.value =
        normalizedPayload.selected_conversation ||
        (preserveMissing ? selectedConversationPayload.value : null);
    }
    if (
      shouldApplyHistoryValue(
        normalizedPayload,
        'conversation_feedback',
        preserveMissing
      )
    ) {
      conversationFeedback.value =
        normalizedPayload.conversation_feedback || null;
    } else if (
      shouldApplyHistoryValue(
        normalizedPayload,
        'selected_conversation',
        preserveMissing
      )
    ) {
      conversationFeedback.value =
        normalizedPayload.selected_conversation?.conversation_feedback || null;
    }
    if (
      shouldApplyHistoryValue(normalizedPayload, 'lead', preserveMissing) &&
      normalizedPayload.lead
    ) {
      lead.value = { ...lead.value, ...normalizedPayload.lead };
    }

    const nextPayloadState = getLeadHistoryPayloadState(normalizedPayload);
    if (
      !preserveMissing ||
      nextPayloadState !== LEAD_HISTORY_LOAD_STATES.EMPTY ||
      historyLoadState.value !== LEAD_HISTORY_LOAD_STATES.READY
    ) {
      historyLoadState.value = nextPayloadState;
    }

    notifyHistoryPayloadApplied();
  };

  const carregarHistorico = async ({
    conversationId = null,
    preserveMissing = false,
    alertOnError = false,
  } = {}) => {
    const id = route.params.id;
    if (!id) return false;
    const requestVersion = beginRequest('history');

    try {
      const { data } = await LeadHistoryAPI.show({
        contact_id: id,
        conversation_id: conversationId,
      });
      if (!isCurrentRequest('history', requestVersion, id)) return false;

      applyHistoryPayload(data?.payload || {}, { preserveMissing });
      return true;
    } catch (error) {
      if (!isCurrentRequest('history', requestVersion, id)) return false;

      const loadState = setLoadErrorState(historyLoadState, error);
      if (!preserveMissing) {
        selectedConversationPayload.value = null;
        conversationFeedback.value = null;
      }
      if (alertOnError) {
        useAlert(loadState.message || historyErrorCopy.value.description);
      }
      return false;
    }
  };

  const carregarHistoricoDaConversa = async conversationId => {
    if (!conversationId || isLoading.value) return false;

    isLoadingConversationHistory.value = true;
    try {
      return await carregarHistorico({
        conversationId,
        preserveMissing: true,
        alertOnError: false,
      });
    } finally {
      if (String(selectedConversationId.value) === String(conversationId)) {
        isLoadingConversationHistory.value = false;
      }
    }
  };

  const carregarLatestPendingSupport = async ({
    alertOnError = false,
  } = {}) => {
    const id = route.params.id;
    if (!id) {
      beginRequest('support');
      setLatestPendingSupportPayload(null);
      pendingSupportLoadState.value = LEAD_HISTORY_LOAD_STATES.EMPTY;
      return false;
    }
    const requestVersion = beginRequest('support');

    pendingSupportLoadState.value = LEAD_HISTORY_LOAD_STATES.LOADING;
    try {
      const { data } = await OnlineDutyAPI.latestPendingSupport({
        contact_id: id,
      });
      if (!isCurrentRequest('support', requestVersion, id)) return false;

      setLatestPendingSupportPayload(pendingSupportPayloadFromResponse(data));
      pendingSupportLoadState.value = latestPendingSupport.value
        ? LEAD_HISTORY_LOAD_STATES.READY
        : LEAD_HISTORY_LOAD_STATES.EMPTY;
      return true;
    } catch (error) {
      if (!isCurrentRequest('support', requestVersion, id)) return false;

      setLatestPendingSupportPayload(null);
      const loadState = setLoadErrorState(pendingSupportLoadState, error);
      if (alertOnError) {
        useAlert(
          loadState.message || 'Não foi possível carregar o plantão de suporte.'
        );
      }
      return false;
    }
  };

  const aplicarVisitasResponse = data => {
    visitas.value = collectionFromResponse(data);
    visitasLoadError.value = false;
    updateCollectionState(visitasLoadState, visitas.value);
  };

  const carregarVisitas = async ({ alertOnError = true } = {}) => {
    const id = route.params.id;
    if (!id) return false;
    const requestVersion = beginRequest('visits');

    try {
      const { data } = await LeadHistoryAPI.visits({ contact_id: id });
      if (!isCurrentRequest('visits', requestVersion, id)) return false;

      aplicarVisitasResponse(data);
      return true;
    } catch (error) {
      if (!isCurrentRequest('visits', requestVersion, id)) return false;

      visitasLoadError.value = true;
      visitas.value = [];
      const loadState = setLoadErrorState(visitasLoadState, error);
      if (alertOnError) {
        useAlert(loadState.message || visitasErrorCopy.value.description);
      }
      return false;
    }
  };

  const carregarNotas = async ({ alertOnError = true } = {}) => {
    const id = route.params.id;
    if (!id) return false;
    const requestVersion = beginRequest('notes');

    isLoadingNotes.value = true;
    notesLoadState.value = LEAD_HISTORY_LOAD_STATES.LOADING;
    try {
      const { data } = await LeadHistoryAPI.notes(id);
      if (!isCurrentRequest('notes', requestVersion, id)) return false;

      contactNotes.value = collectionFromResponse(data);
      updateCollectionState(notesLoadState, contactNotes.value);
      return true;
    } catch (error) {
      if (!isCurrentRequest('notes', requestVersion, id)) return false;

      contactNotes.value = [];
      const loadState = setLoadErrorState(notesLoadState, error);
      if (alertOnError) {
        useAlert(loadState.message || notesErrorCopy.value.description);
      }
      return false;
    } finally {
      if (isCurrentRequest('notes', requestVersion, id)) {
        isLoadingNotes.value = false;
      }
    }
  };

  const refreshTab = async tab => {
    if (isLoading.value) return false;

    if (tab === 'anotacoes') {
      await carregarNotas({ alertOnError: false });
      return true;
    }

    if (!['imoveis', 'historico'].includes(tab)) return false;

    refreshingTab.value = tab;
    try {
      const tasks = [
        carregarHistorico({
          conversationId: selectedConversationId.value,
          preserveMissing: true,
          alertOnError: false,
        }),
      ];
      if (tab === 'historico') {
        tasks.push(carregarVisitas({ alertOnError: false }));
        tasks.push(carregarLatestPendingSupport({ alertOnError: false }));
      }
      await Promise.all(tasks);
      return true;
    } finally {
      if (refreshingTab.value === tab) refreshingTab.value = null;
    }
  };

  const carregar = async () => {
    const id = route.params.id;
    if (!id) return false;
    const requestVersion = beginRequest('page');
    const historyRequestVersion = beginRequest('history');

    isLoading.value = true;
    resetLeadData();
    profileLoadError.value = false;
    conversationLoadError.value = false;
    visitasLoadError.value = false;
    profileLoadState.value = LEAD_HISTORY_LOAD_STATES.LOADING;
    conversationLoadState.value = LEAD_HISTORY_LOAD_STATES.LOADING;
    visitasLoadState.value = LEAD_HISTORY_LOAD_STATES.LOADING;
    historyLoadState.value = LEAD_HISTORY_LOAD_STATES.LOADING;
    try {
      const routeConversationId = routeString(
        route.query?.conversation_id || route.query?.conversation
      );
      const [leadRes, convsRes, visitasRes, historyRes] =
        await Promise.allSettled([
          LeadHistoryAPI.contact(id),
          LeadHistoryAPI.conversations(id),
          LeadHistoryAPI.visits({ contact_id: id }),
          LeadHistoryAPI.show({
            contact_id: id,
            conversation_id: routeConversationId,
          }),
          carregarLatestPendingSupport({ alertOnError: false }),
        ]);

      if (!isCurrentRequest('page', requestVersion, id)) return false;

      if (leadRes.status === 'fulfilled') {
        const leadPayload = leadRes.value.data?.payload || {};
        lead.value = Object.keys(leadPayload).length ? leadPayload : lead.value;
        profileLoadState.value = Object.keys(leadPayload).length
          ? LEAD_HISTORY_LOAD_STATES.READY
          : LEAD_HISTORY_LOAD_STATES.EMPTY;
      } else {
        profileLoadError.value = true;
        const loadState = setLoadErrorState(profileLoadState, leadRes.reason);
        useAlert(loadState.message || profileErrorCopy.value.description);
      }

      if (convsRes.status === 'fulfilled') {
        conversations.value = collectionFromResponse(convsRes.value.data);
        updateCollectionState(conversationLoadState, conversations.value);
        selectedConversationId.value =
          conversations.value.find(
            conversation =>
              String(conversation.id) === String(routeConversationId || '')
          )?.id ||
          conversations.value[0]?.id ||
          null;
      } else {
        conversationLoadError.value = true;
        conversations.value = [];
        selectedConversationId.value = null;
        const loadState = setLoadErrorState(
          conversationLoadState,
          convsRes.reason
        );
        useAlert(loadState.message || conversationErrorCopy.value.description);
      }

      if (visitasRes.status === 'fulfilled') {
        aplicarVisitasResponse(visitasRes.value.data);
      } else {
        visitasLoadError.value = true;
        visitas.value = [];
        const loadState = setLoadErrorState(
          visitasLoadState,
          visitasRes.reason
        );
        useAlert(loadState.message || visitasErrorCopy.value.description);
      }

      if (
        historyRes.status === 'fulfilled' &&
        isCurrentRequest('history', historyRequestVersion, id)
      ) {
        const payload = historyRes.value.data?.payload || {};
        applyHistoryPayload(payload);
      } else if (isCurrentRequest('history', historyRequestVersion, id)) {
        setLoadErrorState(historyLoadState, historyRes.reason);
      }
      await carregarNotas();
      return isCurrentRequest('page', requestVersion, id);
    } catch (error) {
      if (!isCurrentRequest('page', requestVersion, id)) return false;

      profileLoadError.value = true;
      const loadState = setLoadErrorState(profileLoadState, error);
      useAlert(loadState.message || profileErrorCopy.value.description);
      return false;
    } finally {
      if (isCurrentRequest('page', requestVersion, id)) {
        isLoading.value = false;
      }
    }
  };

  return {
    lead,
    conversations,
    visitas,
    crmSummary,
    buyerProfile,
    offers,
    favorites,
    recommendations,
    timeline,
    latestPendingSupport,
    publicVisitRequest,
    historyOriginPayload,
    crmOperations,
    selectedConversationPayload,
    conversationFeedback,
    selectedConversationId,
    messages,
    contactNotes,
    isLoading,
    isLoadingMessages,
    isLoadingConversationHistory,
    isLoadingNotes,
    profileLoadError,
    conversationLoadError,
    visitasLoadError,
    historyLoadState,
    profileLoadState,
    conversationLoadState,
    visitasLoadState,
    notesLoadState,
    pendingSupportLoadState,
    pendingSupportNextStatus,
    refreshingTab,
    profileErrorCopy,
    historyErrorCopy,
    conversationErrorCopy,
    visitasErrorCopy,
    notesErrorCopy,
    historyLoadError,
    notesLoadError,
    pendingSupportLoadError,
    isRefreshingTab,
    setLatestPendingSupportPayload,
    resetLeadData,
    clearSelectedConversationContext,
    prependNote,
    carregarMensagens,
    applyHistoryPayload,
    carregarHistorico,
    carregarHistoricoDaConversa,
    carregarLatestPendingSupport,
    carregarVisitas,
    carregarNotas,
    refreshTab,
    carregar,
  };
}
